import { Box, Flex } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { reportFilterText } from 'Const/report/text';
import SearchComponent from 'UiKit/search';
import FilterSelect from 'UiKit/filter-select';
import FilterYearMonthPicker from 'UiKit/filter-year-month-picker';
import FilterDatepicker from 'UiKit/filter-datepicker';
import { TextRegular, TextSemiBold } from 'UiKit/typography';
import { Condition } from 'Component/others';
import { useReportState } from 'View/report/state';
import useGetBrand from 'Domain/brand/useGetBrand';
import useGetEcommerce from 'Domain/ecommerce/useGetEcommerce';
import useGetAllAccountLivestream from 'Domain/schedule/useGetAllAccountLivestream';
import useGetAccountType from 'Domain/report/useGetAccountType';
import { CloseIcon } from 'Asset/icon';

import getVisibleFilterByTab from 'View/report/helper/getVisibleFilterByTab';
import checkIsResetAllFilter from 'View/report/helper/checkIsResetAllFilter';

import styles from './styles.module.css';

const Filter = ({ selectedTab }) => {
  const { t } = useTranslation('report-live');
  const { filterState, updateFilterState, resetFilterState } = useReportState();
  const filterConfig = getVisibleFilterByTab(selectedTab);
  const isResetAllFilter = checkIsResetAllFilter({ selectedTab, filterState });

  const brandOptions = useGetBrand();
  const platformOptions = useGetEcommerce({ enabled: true });
  const accountOptions = useGetAllAccountLivestream({ brand: filterState[selectedTab]?.brand, platform: filterState[selectedTab]?.platform });
  const accountTypeOptions = useGetAccountType();
  const onUpdateQueryParams = ({ type, value }) => {
    updateFilterState({ [selectedTab]: { ...filterState[selectedTab], [type]: value }, page: 1 });
  };

  const onResetFilter = () => resetFilterState();

  return (
    <Box className={styles['filter__wrapper']}>
      <Flex className={styles['filter__container']}>
        <Flex className={styles['filter-item__container']}>
          <Condition statement={!['brand', 'accountPlatform'].includes(selectedTab)}>
            <SearchComponent maxWidth="337px" onSearch={(search) => updateFilterState({ search, page: 1 })} value={filterState?.search} />
          </Condition>

          <Condition statement={filterConfig?.isBrandFilterVisible}>
            <Box className={styles['filter__item']}>
              <FilterSelect
                isLoading={brandOptions?.isLoading}
                placeholder={t('filter.brand')}
                value={filterState[selectedTab]?.brand}
                clearable={true}
                width="168px"
                options={brandOptions?.data}
                counterLabel={'Brands'}
                onChange={(value) => onUpdateQueryParams({ type: 'brand', value })}
                isMulti
              />
            </Box>
          </Condition>

          <Condition statement={filterConfig?.isYearMonthFilterVisible}>
            <Box className={styles['filter__item']}>
              <FilterYearMonthPicker
                value={filterState[selectedTab]?.yearMonth}
                placeholder={reportFilterText?.yearMonthPlaceholder}
                onChange={(value) => onUpdateQueryParams({ type: 'yearMonth', value })}
              />
            </Box>
          </Condition>

          <Condition statement={filterConfig?.isPlatformFilterVisible}>
            <Box className={styles['filter__item']}>
              <FilterSelect
                isLoading={platformOptions?.isLoading}
                placeholder={t('filter.platform')}
                value={filterState[selectedTab]?.platform}
                clearable={true}
                width="168px"
                options={platformOptions?.data}
                counterLabel={'Platforms'}
                onChange={(value) => onUpdateQueryParams({ type: 'platform', value })}
                isMulti
              />
            </Box>
          </Condition>

          <Condition statement={filterConfig?.isAccountFilterVisible}>
            <Box className={styles['filter__item']}>
              <FilterSelect
                isLoading={accountOptions?.isLoading}
                placeholder={t('filter.account')}
                value={filterState[selectedTab]?.account}
                clearable={true}
                width="168px"
                options={accountOptions?.data}
                counterLabel={'Accounts'}
                onChange={(value) => onUpdateQueryParams({ type: 'account', value })}
                isMulti
              />
            </Box>
          </Condition>

          <Condition statement={filterConfig?.isAccountTypeFilterVisible}>
            <Box className={styles['filter__item']}>
              <FilterSelect
                isLoading={accountTypeOptions?.isLoading}
                placeholder={t('filter.accountType')}
                value={filterState[selectedTab].accountType}
                clearable={true}
                width="168px"
                options={accountTypeOptions?.data}
                counterLabel={'AccountTypes'}
                onChange={(value) => onUpdateQueryParams({ type: 'accountType', value })}
                isMulti
              />
            </Box>
          </Condition>

          <Condition statement={filterConfig?.isDateRangeFilterVisible}>
            <Flex className={`${styles['filter__item']} ${styles['filter__item--date-range']}`}>
              <FilterDatepicker
                placeholder={reportFilterText?.datePlaceholder}
                value={filterState[selectedTab]?.startedAt}
                onChange={(value) => onUpdateQueryParams({ type: 'startedAt', value })}
              />
              <TextRegular>-</TextRegular>
              <FilterDatepicker
                placeholder={reportFilterText?.datePlaceholder}
                value={filterState[selectedTab]?.endedAt}
                onChange={(value) => onUpdateQueryParams({ type: 'endedAt', value })}
              />
            </Flex>
          </Condition>

          <Condition statement={isResetAllFilter}>
            <Flex onClick={onResetFilter} className={styles['reset-filter__container']} data-testid="reporting__clear-filter">
              <CloseIcon size="14px" color="var(--color-semantic-text)" />
              <TextSemiBold>Clear Filters</TextSemiBold>
            </Flex>
          </Condition>
        </Flex>
      </Flex>
    </Box>
  );
};

export default Filter;
